import { useState } from 'react';
import { get, post, session, fmt, ApiError } from '../lib/api';
import { enqueue, flush } from '../lib/outbox';

const REASONS = [
  ['wrong_size', 'Wrong size'], ['changed_mind', 'Changed mind'], ['faulty', 'Faulty'],
  ['not_as_described', 'Not as described'], ['gift_return', 'Gift return'],
  ['duplicate_purchase', 'Duplicate purchase'], ['other', 'Other'],
];

/**
 * Returns / credit notes.
 *
 * The POS is the AUTHORITY on whether a refund is allowed. It validates against
 * the original sale, tells the cashier whether this is a free same-day VOID or a
 * post-settlement REFUND, and offers only the refund routes that are legal for
 * the original tender (POS_MASTER_PLAN.md §4).
 */
export default function Returns() {
  const s = session.get()!;
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [elig, setElig] = useState<any>(null);
  const [qty, setQty] = useState<Record<string, number>>({});
  const [reason, setReason] = useState('wrong_size');
  const [restock, setRestock] = useState('saleable');
  const [route, setRoute] = useState('cash');
  const [rrn, setRrn] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState<any>(null);

  async function find() {
    setError(''); setElig(null); setDone(null);
    try {
      const data = await get<any>(`/api/v1/sales?receiptNo=${encodeURIComponent(search)}&limit=20`);
      setResults(data.items);
      if (!data.items.length) setError('No sale found with that receipt number');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Search failed');
    }
  }

  async function load(saleId: string) {
    setError('');
    try {
      const e = await get<any>(`/api/v1/returns/eligibility/${saleId}`);
      setElig(e);
      setQty({});
      setRoute(e.suggestedRefundRoutes?.[0]?.method ?? 'cash');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the sale');
    }
  }

  const selected = elig?.returnable?.filter((r: any) => (qty[r.saleLineId] ?? 0) > 0) ?? [];
  const refundTotal = selected.reduce(
    (sum: number, r: any) => sum + r.refundPerUnitMinor * (qty[r.saleLineId] ?? 0), 0);

  async function submit() {
    if (!selected.length) { setError('Select at least one item to return'); return; }
    setBusy(true); setError('');
    const id = crypto.randomUUID();
    const payload = {
      id,
      originalSaleId: elig.sale.id,
      storeCode: s.storeCode,
      terminalCode: s.terminalCode,
      reasonCode: reason,
      restockTarget: restock,
      lines: selected.map((r: any) => ({
        saleLineId: r.saleLineId, variantId: r.variantId,
        quantity: qty[r.saleLineId], refundMinor: r.refundPerUnitMinor * qty[r.saleLineId],
      })),
      refunds: [{
        method: route,
        amountMinor: refundTotal,
        ...(route === 'card_terminal' && rrn ? { rrn, authCode: rrn } : {}),
        ...(route.startsWith('card') ? { cardScheme: elig.originalTenders?.find((t: any) => t.method.startsWith('card'))?.cardScheme } : {}),
      }],
      deviceTime: new Date().toISOString(),
    };

    try {
      // Try the server first so policy errors surface immediately; if offline,
      // queue it like any other document.
      const res = await post<any>('/api/v1/returns', payload);
      setDone(res);
      setElig(null);
    } catch (err) {
      if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
        setError(err.message);
      } else {
        enqueue('return', id, payload);
        void flush();
        setDone({ creditNoteNo: 'QUEUED', totalMinor: refundTotal, queued: true });
        setElig(null);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-4">
      <h1 className="text-xl font-semibold">Returns &amp; exchanges</h1>

      <div className="card p-4">
        <label className="label">Find the original sale</label>
        <div className="flex gap-2">
          <input className="input flex-1" placeholder="Receipt number, e.g. BH21101-01-26-000001"
            value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void find(); }} />
          <button className="btn-primary" onClick={() => void find()}>Search</button>
        </div>
        <div className="text-[11px] text-gray-500 mt-2">
          Every return must be linked to its original sale. Returns without a receipt
          require manager permission and are reported on.
        </div>
      </div>

      {results.length > 0 && !elig && (
        <div className="card divide-y divide-gray-800">
          {results.map((r) => (
            <button key={r.id} className="w-full px-4 py-3 flex justify-between items-center hover:bg-gray-800/50 text-left"
              onClick={() => void load(r.id)}>
              <div>
                <div className="font-medium">{r.receiptNo}</div>
                <div className="text-xs text-gray-500">{new Date(r.serverTime).toLocaleString('en-GB')}</div>
              </div>
              <div className="money font-semibold">{fmt(r.totalMinor)}</div>
            </button>
          ))}
        </div>
      )}

      {elig && (
        <>
          {/* Void vs refund guidance — staff should not have to know this rule. */}
          <div className={`card p-4 border-l-4 ${elig.operation === 'VOID' ? 'border-l-emerald-500' : 'border-l-amber-500'}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">{elig.sale.receiptNo}</div>
                <div className="text-xs text-gray-500">
                  Sold {new Date(elig.sale.soldAt).toLocaleString('en-GB')} · {elig.ageDays} day(s) ago
                  {!elig.withinWindow && <span className="text-amber-400"> · outside the 14-day window</span>}
                </div>
              </div>
              <div className="text-right">
                <span className={`badge ${elig.operation === 'VOID'
                  ? 'bg-emerald-900/50 text-emerald-300' : 'bg-amber-900/50 text-amber-300'}`}>
                  {elig.operation}
                </span>
                <div className="text-[11px] text-gray-500 mt-1">
                  {elig.operation === 'VOID'
                    ? 'Same day — no fee, customer never debited'
                    : 'Settled — fees apply, customer waits days'}
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="px-4 py-2 border-b border-gray-800 text-sm font-medium">Items</div>
            <table className="w-full text-sm">
              <thead><tr>
                <th className="th">Item</th><th className="th">Bought</th>
                <th className="th">Returned</th><th className="th">Refund each</th><th className="th">Return qty</th>
              </tr></thead>
              <tbody>
                {elig.returnable.map((r: any) => (
                  <tr key={r.saleLineId}>
                    <td className="td">
                      <div>{r.title}</div>
                      <div className="text-[11px] text-gray-500">{r.sku}</div>
                    </td>
                    <td className="td money">{r.quantity}</td>
                    <td className="td money text-gray-500">{r.returnedQty}</td>
                    <td className="td money">{fmt(r.refundPerUnitMinor)}</td>
                    <td className="td">
                      {r.remainingQty > 0 ? (
                        <input type="number" min={0} max={r.remainingQty}
                          className="input w-20 py-1 money"
                          value={qty[r.saleLineId] ?? 0}
                          onChange={(e) => setQty((p) => ({
                            ...p, [r.saleLineId]: Math.min(r.remainingQty, Math.max(0, Number(e.target.value) || 0)),
                          }))} />
                      ) : <span className="text-xs text-gray-600">fully returned</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="card p-4">
              <label className="label">Reason</label>
              <select className="input w-full" value={reason} onChange={(e) => setReason(e.target.value)}>
                {REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="card p-4">
              <label className="label">Where does the stock go?</label>
              <select className="input w-full" value={restock} onChange={(e) => setRestock(e.target.value)}>
                <option value="saleable">Back to saleable stock</option>
                <option value="damaged">Damaged / quarantine (not sellable)</option>
                <option value="warehouse">Transfer to warehouse</option>
              </select>
              {restock !== 'saleable' && (
                <div className="text-[11px] text-amber-400 mt-1.5">
                  Item will NOT return to sellable stock.
                </div>
              )}
            </div>
            <div className="card p-4">
              <label className="label">Refund route</label>
              <select className="input w-full" value={route} onChange={(e) => setRoute(e.target.value)}>
                {elig.suggestedRefundRoutes?.map((r: any) => (
                  <option key={r.method} value={r.method}>
                    {r.method.replace('_', ' ')} (original tender)
                  </option>
                ))}
                <option value="store_credit">Store credit</option>
                <option value="cash">Cash</option>
              </select>
              <div className="text-[11px] text-gray-500 mt-1.5">
                Paid by: {elig.originalTenders?.map((t: any) => t.method.replace('_', ' ')).join(', ')}
              </div>
            </div>
          </div>

          {route === 'card_terminal' && (
            <div className="card p-4 border-l-4 border-l-amber-500">
              <div className="text-sm font-medium mb-1">Terminal refund</div>
              <div className="text-xs text-gray-400 mb-2">
                Perform the {elig.operation} on the CrediMax terminal for
                <span className="money font-semibold text-gray-200"> {fmt(refundTotal)}</span>,
                then enter the RRN here. Until the RRN is entered this credit note stays
                on the daily exceptions report.
              </div>
              <input className="input w-full" placeholder="Terminal RRN / approval code"
                value={rrn} onChange={(e) => setRrn(e.target.value)} />
            </div>
          )}

          <div className="card p-4 flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-400">Refund total</div>
              <div className="money text-2xl font-bold">{fmt(refundTotal)}</div>
              <div className="text-[11px] text-gray-500">
                includes VAT {fmt(Math.round((refundTotal * 1000) / 11000))}
              </div>
            </div>
            <div className="flex gap-2">
              <button className="btn-ghost" onClick={() => setElig(null)}>Cancel</button>
              <button className="btn-primary h-11" disabled={busy || refundTotal <= 0} onClick={() => void submit()}>
                {busy ? 'Processing…' : 'Issue credit note'}
              </button>
            </div>
          </div>
        </>
      )}

      {error && (
        <div className="text-sm text-red-300 bg-red-950/50 border border-red-900 rounded-lg px-4 py-3">{error}</div>
      )}

      {done && (
        <div className="card p-5 border-l-4 border-l-emerald-500">
          <div className="font-medium text-emerald-300">Credit note issued</div>
          <div className="text-sm text-gray-300 mt-1">
            {done.creditNoteNo} · <span className="money">{fmt(done.totalMinor)}</span>
          </div>
          {done.refundAuthorisation && (
            <div className="mt-3 bg-gray-950 border border-gray-700 rounded p-3 text-sm">
              <div className="font-medium text-amber-300 mb-1">
                Refund authorisation {done.refundAuthorisation.code}
              </div>
              <div className="text-xs text-gray-400">{done.refundAuthorisation.instruction}</div>
              <div className="money mt-1">Amount: {fmt(done.refundAuthorisation.amountMinor)}</div>
            </div>
          )}
          {done.settlementState === 'pending_settlement' && (
            <div className="mt-2 text-xs text-amber-300">
              PENDING SETTLEMENT — enter the terminal RRN to close this out.
            </div>
          )}
          {done.warnings?.map((w: string, i: number) => (
            <div key={i} className="mt-2 text-xs text-amber-300">{w}</div>
          ))}
          <button className="btn-ghost mt-3" onClick={() => { setDone(null); setResults([]); setSearch(''); }}>
            New return
          </button>
        </div>
      )}
    </div>
  );
}
